import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-32 bg-[#FAFAFA]">
      {/* Header */}
      <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-[#E8EAE8] z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-[#3A5A40] hover:text-[#2B4330] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} strokeWidth={1.5} />
          </button>
          <h1 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Politique de Confidentialité
          </h1>
        </div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-6 py-8"
      >
        <div className="bg-white rounded-2xl border border-[#E8EAE8] p-8 shadow-sm">
          <div className="text-sm text-[#6B726B] mb-8">
            Dernière mise à jour / Last updated: April 2026
          </div>

          {/* FRANÇAIS */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-6 text-[#3A5A40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              🇫🇷 Politique de Confidentialité (Français)
            </h2>

            <p className="mb-4 text-[#1A1C1A] leading-relaxed">
              AlQuran.fr ("nous", "notre application") s'engage à protéger votre vie privée. Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Données collectées</h3>
            <p className="mb-2 text-[#1A1C1A]">Notre application peut collecter les données suivantes :</p>
            <ul className="list-disc pl-6 mb-4 text-[#1A1C1A] space-y-2">
              <li><strong>Identifiant publicitaire (IDFA)</strong> — utilisé par Google AdMob pour afficher des publicités pertinentes.</li>
              <li><strong>Données d'utilisation</strong> — interactions avec l'application (pages visitées, récitateurs écoutés) via Google Analytics.</li>
              <li><strong>Données de diagnostic</strong> — rapports de crash anonymes via Firebase Crashlytics.</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Utilisation des données</h3>
            <ul className="list-disc pl-6 mb-4 text-[#1A1C1A] space-y-2">
              <li>Afficher des publicités via Google AdMob</li>
              <li>Améliorer l'expérience utilisateur</li>
              <li>Analyser l'utilisation de l'application</li>
              <li>Corriger les bugs et améliorer les performances</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Partage des données</h3>
            <p className="mb-2 text-[#1A1C1A]">Nous ne vendons pas vos données personnelles. Vos données peuvent être partagées avec :</p>
            <ul className="list-disc pl-6 mb-4 text-[#1A1C1A] space-y-2">
              <li><strong>Google AdMob</strong> — pour la diffusion de publicités. <a href="https://policies.google.com/privacy" className="text-[#D4A373] hover:underline" target="_blank" rel="noopener noreferrer">Politique Google</a></li>
              <li><strong>Google Analytics</strong> — pour l'analyse d'utilisation.</li>
              <li><strong>Firebase</strong> — pour les rapports de crash.</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Suivi publicitaire (ATT)</h3>
            <p className="mb-4 text-[#1A1C1A]">
              Sur iOS 14+, nous demandons votre autorisation avant d'accéder à votre identifiant publicitaire (IDFA), conformément à la politique App Tracking Transparency d'Apple. Vous pouvez refuser à tout moment dans les Réglages de votre iPhone.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Conservation des données</h3>
            <p className="mb-4 text-[#1A1C1A]">
              Les données analytiques sont conservées pendant 26 mois maximum. Les données publicitaires sont gérées par Google conformément à leur politique de confidentialité.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Vos droits</h3>
            <ul className="list-disc pl-6 mb-4 text-[#1A1C1A] space-y-2">
              <li>Désactiver le suivi publicitaire dans Réglages → Confidentialité → Suivi</li>
              <li>Désactiver la personnalisation des annonces Google : <a href="https://adssettings.google.com/" className="text-[#D4A373] hover:underline" target="_blank" rel="noopener noreferrer">adssettings.google.com</a></li>
              <li>Contacter : <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a></li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Enfants</h3>
            <p className="mb-4 text-[#1A1C1A]">
              Notre application ne collecte pas sciemment de données auprès d'enfants de moins de 13 ans.
            </p>
          </section>

          {/* ENGLISH */}
          <section className="mb-12 pt-8 border-t border-[#E8EAE8]">
            <h2 className="text-2xl font-medium mb-6 text-[#3A5A40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              🇬🇧 Privacy Policy (English)
            </h2>

            <p className="mb-4 text-[#1A1C1A] leading-relaxed">
              AlQuran.fr ("we", "our app") is committed to protecting your privacy. This policy explains how we collect, use, and protect your information.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Data Collected</h3>
            <ul className="list-disc pl-6 mb-4 text-[#1A1C1A] space-y-2">
              <li><strong>Advertising Identifier (IDFA)</strong> — used by Google AdMob to show relevant ads.</li>
              <li><strong>Usage Data</strong> — app interactions via Google Analytics.</li>
              <li><strong>Diagnostic Data</strong> — anonymous crash reports via Firebase Crashlytics.</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Use of Data</h3>
            <ul className="list-disc pl-6 mb-4 text-[#1A1C1A] space-y-2">
              <li>Display advertisements via Google AdMob</li>
              <li>Improve user experience</li>
              <li>Analyze app usage</li>
              <li>Fix bugs and improve performance</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Data Sharing</h3>
            <p className="mb-4 text-[#1A1C1A]">
              We do not sell your personal data. Data may be shared with Google AdMob, Google Analytics, and Firebase solely for app functionality.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Ad Tracking (ATT)</h3>
            <p className="mb-4 text-[#1A1C1A]">
              On iOS 14+, we request your permission before accessing your advertising identifier (IDFA). You can opt out at any time in iPhone Settings → Privacy → Tracking.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Your Rights</h3>
            <ul className="list-disc pl-6 mb-4 text-[#1A1C1A] space-y-2">
              <li>Disable ad tracking: Settings → Privacy → Tracking</li>
              <li>Opt out of Google ad personalization: <a href="https://adssettings.google.com/" className="text-[#D4A373] hover:underline" target="_blank" rel="noopener noreferrer">adssettings.google.com</a></li>
              <li>Contact us: <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a></li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40]">Children</h3>
            <p className="mb-4 text-[#1A1C1A]">
              Our app does not knowingly collect data from children under 13.
            </p>
          </section>

          {/* ARABIC */}
          <section className="mb-12 pt-8 border-t border-[#E8EAE8]" dir="rtl">
            <h2 className="text-2xl font-medium mb-6 text-[#3A5A40] text-right" style={{ fontFamily: 'Amiri, serif' }}>
              🇸🇦 سياسة الخصوصية (العربية)
            </h2>

            <p className="mb-4 text-[#1A1C1A] leading-relaxed text-right" style={{ fontFamily: 'Amiri, serif' }}>
              يلتزم تطبيق AlQuran.fr بحماية خصوصيتك. توضح هذه السياسة كيفية جمع معلوماتك واستخدامها وحمايتها.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40] text-right">البيانات المجمعة</h3>
            <ul className="list-disc pr-6 mb-4 text-[#1A1C1A] space-y-2 text-right" style={{ fontFamily: 'Amiri, serif' }}>
              <li><strong>معرّف الإعلانات (IDFA)</strong> — تستخدمه Google AdMob لعرض إعلانات ذات صلة.</li>
              <li><strong>بيانات الاستخدام</strong> — تفاعلات التطبيق عبر Google Analytics.</li>
              <li><strong>بيانات التشخيص</strong> — تقارير الأعطال المجهولة عبر Firebase.</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40] text-right">استخدام البيانات</h3>
            <ul className="list-disc pr-6 mb-4 text-[#1A1C1A] space-y-2 text-right" style={{ fontFamily: 'Amiri, serif' }}>
              <li>عرض الإعلانات عبر Google AdMob</li>
              <li>تحسين تجربة المستخدم</li>
              <li>تحليل استخدام التطبيق</li>
              <li>إصلاح الأخطاء وتحسين الأداء</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3 text-[#3A5A40] text-right">حقوقك</h3>
            <ul className="list-disc pr-6 mb-4 text-[#1A1C1A] space-y-2 text-right" style={{ fontFamily: 'Amiri, serif' }}>
              <li>تعطيل تتبع الإعلانات: الإعدادات ← الخصوصية ← التتبع</li>
              <li>البريد الإلكتروني: <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a></li>
            </ul>
          </section>

          {/* Other Languages - Condensed */}
          <section className="pt-8 border-t border-[#E8EAE8]">
            <h2 className="text-xl font-medium mb-6 text-[#3A5A40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Other Languages
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-[#3A5A40] mb-2">🇩🇪 Datenschutzerklärung (Deutsch)</h3>
                <p className="text-[#1A1C1A] text-sm">
                  AlQuran.fr verpflichtet sich zum Schutz Ihrer Privatsphäre. Wir erheben Werbedaten (IDFA) über Google AdMob, Nutzungsdaten über Google Analytics und anonyme Absturzberichte über Firebase. Wir verkaufen Ihre Daten nicht. Kontakt: <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a>
                </p>
              </div>

              <div>
                <h3 className="font-medium text-[#3A5A40] mb-2">🇪🇸 Política de Privacidad (Español)</h3>
                <p className="text-[#1A1C1A] text-sm">
                  AlQuran.fr se compromete a proteger su privacidad. Recopilamos datos publicitarios (IDFA) mediante Google AdMob, datos de uso mediante Google Analytics e informes de fallos anónimos mediante Firebase. No vendemos sus datos. Contacto: <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a>
                </p>
              </div>

              <div>
                <h3 className="font-medium text-[#3A5A40] mb-2">🇹🇷 Gizlilik Politikası (Türkçe)</h3>
                <p className="text-[#1A1C1A] text-sm">
                  AlQuran.fr gizliliğinizi korumayı taahhüt eder. Google AdMob aracılığıyla reklam verileri (IDFA), Google Analytics aracılığıyla kullanım verileri ve Firebase aracılığıyla anonim çökme raporları topluyoruz. Verilerinizi satmıyoruz. İletişim: <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a>
                </p>
              </div>

              <div>
                <h3 className="font-medium text-[#3A5A40] mb-2">🇮🇹 Informativa sulla Privacy (Italiano)</h3>
                <p className="text-[#1A1C1A] text-sm">
                  AlQuran.fr si impegna a proteggere la tua privacy. Raccogliamo dati pubblicitari (IDFA) tramite Google AdMob, dati di utilizzo tramite Google Analytics e report di crash anonimi tramite Firebase. Non vendiamo i tuoi dati. Contatto: <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a>
                </p>
              </div>

              <div>
                <h3 className="font-medium text-[#3A5A40] mb-2">🇵🇹 Política de Privacidade (Português)</h3>
                <p className="text-[#1A1C1A] text-sm">
                  AlQuran.fr compromete-se a proteger a sua privacidade. Recolhemos dados publicitários (IDFA) através do Google AdMob, dados de utilização através do Google Analytics e relatórios de falhas anónimos através do Firebase. Não vendemos os seus dados. Contacto: <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a>
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="mt-12 pt-8 border-t border-[#E8EAE8] text-center">
            <h3 className="text-lg font-medium mb-4 text-[#3A5A40]">Contact</h3>
            <p className="text-[#1A1C1A]">
              Email: <a href="mailto:contact@alquran.fr" className="text-[#D4A373] hover:underline">contact@alquran.fr</a>
            </p>
            <p className="text-[#1A1C1A]">
              Website: <a href="https://alquran.fr/" className="text-[#D4A373] hover:underline">https://alquran.fr</a>
            </p>
          </section>
        </div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;
